import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Image } from '@codes/ui-kit/components/ui/image';
import { calcTestSignature } from '@lemoncloud/lemon-web-core';

import { GAEvents, trackEvent } from '@codes/analytics';
import { CodesLogoImage, LoginCoverDarkImage, LoginCoverImage, LoginGoogleIcon, LogoLightImage } from '@codes/assets';
import { VerifyNativeTokenBody, useSnsTestLoginQuery } from '@codes/auth';
import { useLoader } from '@codes/loader';
import { Input } from '@codes/ui-kit';
import { isProdEnvironment, useWebCoreStore } from '@codes/web-core';

export const LoginPage = () => {
    const { setIsLoading } = useLoader();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const { setIsAuthenticated, setProfile } = useWebCoreStore(state => state);
    const onClickLogin = (provider: string) => {
        trackEvent(GAEvents.LOGIN_CLICK, { method: provider });
        setIsLoading(true);
        const HOST = import.meta.env.VITE_HOST.toLowerCase();
        const SOCIAL_OAUTH = import.meta.env.VITE_SOCIAL_OAUTH_ENDPOINT.toLowerCase();
        const redirectUrl = `${HOST}/auth/oauth-response`;
        window.location.replace(`${SOCIAL_OAUTH}/oauth/${provider}/authorize?redirect=${redirectUrl}`);
    };

    const { mutateAsync: snsTestLogin } = useSnsTestLoginQuery(
        _ => {
            setIsAuthenticated(true);
            navigate('/w', { replace: true });
            setIsLoading(false);
        },
        e => {
            confirm(e?.message || '에러입니다.');
            setIsLoading(false);
        }
    );

    const isProd = isProdEnvironment();

    const [snsTestUid, setSnsTestUid] = useState('');
    const isDisabled = !snsTestUid;

    const onClickSnsTestLogin = () => {
        const date = new Date();

        const body: VerifyNativeTokenBody = {
            provider: 'test',
            idToken: snsTestUid,
            refreshToken: date.toISOString(),
            signature: calcTestSignature(
                {
                    authId: snsTestUid,
                    accountId: snsTestUid,
                    identityId: snsTestUid,
                    identityToken: '*test-native-token-240828*',
                },
                date.toISOString()
            ),
        };
        setIsLoading(true);
        snsTestLogin(body);
    };

    const onEnterSnsTestInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== 'Enter' || !snsTestLogin) {
            return;
        }

        onClickSnsTestLogin();
    };

    return (
        <div className="w-screen h-screen grid grid-cols-2 max-md:grid-cols-1 p-6 items-center justify-center bg-background">
            <div className="h-full justify-self-center w-full flex flex-col items-center max-w-[420px] max-h-[900px] py-20">
                <div className="w-full flex flex-col items-center md:pt-[60%]">
                    <Image className="h-10" src={LogoLightImage} darkSrc={CodesLogoImage} />
                    <div className="mt-[22px] mb-[62px] text-gray-700 !dark:text-gray-300 text-[23px] font-medium whitespace-nowrap">
                        {t('auth.social_login')}
                    </div>
                    <div className="w-full flex flex-col gap-[19px]">
                        <button
                            className="h-[54px] rounded-[34px] border border-gradient shadow-[0px_2px_4px_0px_rgba(0,0,0,0.06)]"
                            onClick={() => onClickLogin('google')}
                        >
                            <div className="px-7 flex items-center justify-center">
                                <Image className="ml-auto" src={LoginGoogleIcon} />
                                <div className="flex-1 pr-5 font-medium">{t('auth.google_login')}</div>
                            </div>
                        </button>
                        {/* TODO:kakao login button */}
                        {/* <button className="h-[54px] rounded-[34px] border border-gradient shadow-[0px_2px_4px_0px_rgba(0,0,0,0.06)]">
                        <div className="px-7 flex items-center justify-center">
                            <Image className="ml-auto" src={LoginKakaoIcon} />
                            <div className="flex-1 pr-5 font-medium">{t('auth.kakao_login')}</div>
                        </div>
                    </button> */}
                    </div>
                    {!isProd && (
                        <div className="w-full flex flex-col gap-3 mt-11">
                            <Input
                                type="text"
                                className="border p-3 rounded-[5px]"
                                placeholder="아무거나 입력해주세요"
                                value={snsTestUid}
                                onChange={e => setSnsTestUid(e.target.value)}
                                onKeyDown={onEnterSnsTestInput}
                            />
                            <button
                                className={`w-full py-3 bg-blue-600 font-bold text-white rounded-[5px] ${
                                    isDisabled ? 'bg-gray-300' : 'hover:bg-blue-400 bg-blue-600'
                                } transition-all`}
                                disabled={isDisabled}
                                onClick={onClickSnsTestLogin}
                            >
                                SNS-TEST LOGIN
                            </button>
                        </div>
                    )}
                </div>

                <div className="text-[18px] text-gray-500 !dark:text-gray-700 md:mt-auto mt-10">
                    From Infrastructure to Scalable Microservices
                </div>
            </div>
            <Image
                className="max-md:hidden h-full max-h-[900px] object-contain"
                src={LoginCoverImage}
                darkSrc={LoginCoverDarkImage}
            />
        </div>
    );
};
